import { Component, inject, OnInit, signal, ViewEncapsulation } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { SysadminUpdate, UserCreate } from '../../../model/user.model';
import { MessagingService } from '../../../components/messaging/messaging.service';
import { ToolbarService } from '../../../services/toolbar.service';
import { EscapeDialogHelper } from '../../../helpers/escape-dialog.helper';
import { SysAdminService } from '../../../services/sys-admin.service';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatDivider } from '@angular/material/divider';
import { MatError, MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { BrowserAutoFillDisabledDirective } from '../../../directives/browser-auto-fill-disabled.directive';

@Component({
  selector: 'app-edit-sysadmin',
  templateUrl: './edit-sysadmin.component.html',
  styleUrls: ['./edit-sysadmin.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    MatDialogTitle,
    MatIconButton,
    MatDialogClose,
    MatIcon,
    MatDivider,
    ReactiveFormsModule,
    MatDialogContent,
    MatFormField,
    MatLabel,
    MatInput,
    BrowserAutoFillDisabledDirective,
    MatError,
    MatDialogActions,
    MatButton
  ]
})
export class EditSysadminComponent implements OnInit {
  #dialogRef = inject(MatDialogRef<EditSysadminComponent>);
  #escapeDialogHelper = inject(EscapeDialogHelper);
  #fb = inject(FormBuilder);
  #messagingService = inject(MessagingService);
  #sysAdminService = inject(SysAdminService);
  #toolbarService = inject(ToolbarService);

  data = inject(MAT_DIALOG_DATA);
  sysadminForm: FormGroup;

  pending = signal<boolean>(false);

  ngOnInit(): void {
    this.sysadminForm = this.#fb.group({
      username: [this.data?.username || '', Validators.required],
      firstName: [this.data?.firstName || '', Validators.required],
      lastName: [this.data?.lastName || '', Validators.required],
      email: [
        this.data?.emailAddress || '',
        [
          Validators.required,
          Validators.pattern(
            '^([^@\\s\\."\'\\(\\)\\[\\]\\{\\}\\\\/,:;]+\\.)*[^@\\s\\."\'\\(\\)\\[\\]\\{\\}\\\\/,:;]' +
              '+@[^@\\s\\."\'\\(\\)\\[\\]\\{\\}\\\\/,:;]+(\\.[^@\\s\\."\'\\(\\)\\[\\]\\{\\}\\\\/,:;]+)+$'
          )
        ]
      ]
    });

    if (this.data) {
      this.username.disable();
    }

    this.#escapeDialogHelper.escapeDialog(this.#dialogRef);
  }

  saveUpdateSysadmin(): void {
    this.#toolbarService.setCloudState('SAVING');
    this.pending.set(true);

    if (!this.data) {
      this.#sysAdminService
        .createSysadmin({
          username: this.username.value.trim(),
          firstName: this.firstName.value.trim(),
          lastName: this.lastName.value.trim(),
          email: this.email.value.trim()
        } as UserCreate)
        .subscribe({
          next: () => this.#dialogRef.close('created'),
          error: (error) => {
            error.error === 'DUPLICATE_USERNAME'
              ? this.username.setErrors({ usernameDuplicate: true })
              : this.#messagingService.error('Could not create the administrator.');

            this.#toolbarService.setCloudState('RESTING');
            this.pending.set(false);
          },
          complete: () => {
            this.#toolbarService.setCloudState('RESTING');
            this.pending.set(false);
          }
        });
    } else {
      const userUpdate: SysadminUpdate = this.sysadminForm.getRawValue();
      this.#sysAdminService.updateAndReinviteSysadmin(this.data?.username, userUpdate).subscribe({
        next: () => this.#dialogRef.close('updated'),
        error: () => {
          this.#messagingService.error('Could not update administrator.');
          this.#toolbarService.setCloudState('RESTING');
          this.pending.set(false);
        },
        complete: () => {
          this.#toolbarService.setCloudState('RESTING');
          this.pending.set(false);
        }
      });
    }
  }

  get username() {
    return this.sysadminForm.get('username');
  }
  get email() {
    return this.sysadminForm.get('email');
  }
  get firstName() {
    return this.sysadminForm.get('firstName');
  }
  get lastName() {
    return this.sysadminForm.get('lastName');
  }
}
